<template>
    <div>
        <div
            v-if="!activeItem"
            class="review-process-complete-state text-center py-3 px-2"
        >
            <div class="mb-1">
                <b-badge variant="success">
                    {{ items.length }} / {{ items.length }}
                </b-badge>
            </div>
            <h5 class="mb-1">
                {{ $t('Component(ReviewProcess):all-documents-reviewed') }}
            </h5>
            <p class="text-muted mb-0">
                {{ $t('Component(ReviewProcess):all-documents-reviewed-description') }}
            </p>
        </div>

        <div
            v-else
            class="mb-1 d-flex justify-content-between align-items-center"
        >
            <div>
                <strong>{{ $t('Component(VerificationDocuments):label') }}</strong>
                <span class="text-muted ml-50">{{ currentPosition }} / {{ items.length }}</span>
            </div>
            <b-badge :variant="statusVariant(activeItem.status)">
                {{ activeItem.status || '-' }}
            </b-badge>
        </div>

        <ReviewItemPanel
            v-if="activeItem"
            ref="activeItemPanel"
            :key="activeItem.id"
            :process-id="process.processId"
            :supplier-id="supplierId"
            :item="activeItem"
            :review-capabilities="reviewCapabilities"
            :show-actions="false"
            :show-cancel="false"
            default-status-on-open="reviewed"
            @cancel="$emit('cancel')"
            @saved="onItemSaved"
        />
    </div>
</template>

<script>
import { BBadge } from 'bootstrap-vue'
import ReviewItemPanel from './ReviewItemPanel.vue'

function deepClone(value) {
    return JSON.parse(JSON.stringify(value || {}))
}

export default {
    name: 'DocumentComplianceReviewProcessFlow',
    components: {
        BBadge,
        ReviewItemPanel,
    },
    props: {
        process: {
            type: Object,
            required: true,
        },
        reviewCapabilities: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            items: [],
            activeItemId: null,
        }
    },
    computed: {
        supplierId() {
            const metadata = this.process && this.process.externalContext && this.process.externalContext.metadata
                ? this.process.externalContext.metadata
                : {}

            return metadata.supplierId || null
        },
        activeItem() {
            return this.items.find(item => item && item.id === this.activeItemId) || null
        },
        currentPosition() {
            const index = this.items.findIndex(item => item && item.id === this.activeItemId)
            return index >= 0 ? index + 1 : 0
        },
        hasActiveItem() {
            return !!this.activeItem
        },
        allItemsReviewed() {
            return this.items.length > 0 && this.items.every(item => item && item.status === 'reviewed')
        },
    },
    watch: {
        process: {
            immediate: true,
            deep: true,
            handler(value) {
                this.items = value && Array.isArray(value.items) ? deepClone(value.items) : []
                this.setInitialActiveItem()
            },
        },
    },
    methods: {
        setInitialActiveItem() {
            const pending = this.items.find(item => item && item.status !== 'reviewed')

            this.activeItemId = pending ? pending.id : null
            this.$nextTick(() => {
                this.emitStateChange()
            })
        },
        onItemSaved(savedItem) {
            const index = this.items.findIndex(item => item && item.id === savedItem.id)
            if (index >= 0) {
                this.$set(this.items, index, savedItem)
            }

            this.$emit('item-saved', savedItem)
            this.moveToNextItem(savedItem.id)
            this.emitStateChange()
        },
        moveToNextItem(savedItemId) {
            const savedIndex = this.items.findIndex(item => item && item.id === savedItemId)
            const afterSaved = this.items.slice(savedIndex + 1).find(item => item && item.status !== 'reviewed')
            const beforeSaved = this.items.slice(0, Math.max(savedIndex, 0)).find(item => item && item.status !== 'reviewed')
            const next = afterSaved || beforeSaved || null

            this.activeItemId = next ? next.id : null
        },
        saveActiveItem() {
            if (!this.$refs.activeItemPanel) {
                return Promise.resolve()
            }

            return this.$refs.activeItemPanel.save()
        },
        emitStateChange() {
            this.$emit('state-change', {
                hasActiveItem: this.hasActiveItem,
                allItemsReviewed: this.allItemsReviewed,
            })
        },
        statusVariant(status) {
            if (status === 'reviewed') {
                return 'success'
            }

            if (status === 'pending') {
                return 'warning'
            }

            return 'secondary'
        },
    },
}
</script>

<style scoped>
.review-process-complete-state {
    min-height: 220px;
    display: flex;
    flex-direction: column;
    justify-content: center;
}
</style>
